import React, { useState } from 'react';
import { BuildComponentTypeDefinition } from '../types/Enums';
import { BASE_TYPE_ICON_MAP } from '../Helpers/componentTypeIconUtils';

interface ComponentTypeIconProps {
    componentType: string | undefined;
    buildComponentTypes: Record<string, BuildComponentTypeDefinition>;
    className?: string;
    style?: React.CSSProperties;
    alt?: string;
}

/**
 * Component that displays an icon for a component type with automatic fallback:
 * 1. Tries specific component type icon (types/{componentType}.png)
 * 2. Falls back to BaseType icon (Basetypes/{baseType}.png)
 * 3. Falls back to generic placeholder
 * 
 * This handles the case where custom component types created in the UI don't have
 * corresponding icon files - they'll automatically show their BaseType icon instead.
 */
const ComponentTypeIcon: React.FC<ComponentTypeIconProps> = ({
    componentType,
    buildComponentTypes,
    className,
    style,
    alt
}) => {
    const [currentSrc, setCurrentSrc] = useState<string>('');
    const [fallbackLevel, setFallbackLevel] = useState<number>(0);

    // Get the icon paths (all paths are full paths starting with /Images/)
    const getIconPaths = (): string[] => {
        if (!componentType) {
            return ['/Images/placeholder.png'];
        }

        const paths: string[] = [];

        // First, try the specific component type icon
        paths.push(`/Images/types/${componentType}.png`);

        // If we have the component type definition, add BaseType fallback
        const typeDef = buildComponentTypes[componentType];
        if (typeDef) {
            const baseTypePath = BASE_TYPE_ICON_MAP[typeDef.baseType];
            if (baseTypePath && baseTypePath !== paths[0]) {
                paths.push(baseTypePath);
            }
        }

        // Final fallback
        paths.push('/Images/placeholder.png');

        return paths;
    };

    const iconPaths = getIconPaths();
    const initialSrc = iconPaths[0] || '/Images/placeholder.png';

    // Initialize with first path
    if (currentSrc === '') {
        setCurrentSrc(initialSrc);
    }

    const handleError = () => {
        const nextLevel = fallbackLevel + 1;
        if (nextLevel < iconPaths.length) {
            setFallbackLevel(nextLevel);
            setCurrentSrc(iconPaths[nextLevel]);
        }
    };

    return (
        <img
            src={currentSrc}
            alt={alt || componentType || 'Component icon'}
            className={className}
            style={style}
            onError={handleError}
        />
    );
};

export default ComponentTypeIcon;

